/**
 * The periods of the reports: days and weeks, months back from now, and the fiscal year
 * (1.10.-30.9.) with its quarters (Q1 = October-December). A period runs from its start up
 * to its end, the end not included, in local time.
 */
export interface IReportPeriod {
  // Unique key of the choice, e.g. 'months-3' or 'quarter-2025-1'
  key: string;
  from: Date;
  to: Date;
}

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

// The week starts on Monday
const startOfWeek = (date: Date): Date => {
  const day = startOfDay(date);
  return addDays(day, -((day.getDay() + 6) % 7));
};

/** The fiscal year that contains the date, by the year it starts in (e.g. 2025 = 1.10.2025-30.9.2026) */
export const fiscalYearOf = (date: Date): number =>
  date.getMonth() >= 9 ? date.getFullYear() : date.getFullYear() - 1;

export const today = (now: Date): IReportPeriod => ({
  from: startOfDay(now),
  key: 'today',
  to: addDays(startOfDay(now), 1),
});

export const yesterday = (now: Date): IReportPeriod => ({
  from: addDays(startOfDay(now), -1),
  key: 'yesterday',
  to: startOfDay(now),
});

export const thisWeek = (now: Date): IReportPeriod => ({
  from: startOfWeek(now),
  key: 'this-week',
  to: addDays(startOfWeek(now), 7),
});

export const lastWeek = (now: Date): IReportPeriod => ({
  from: addDays(startOfWeek(now), -7),
  key: 'last-week',
  to: startOfWeek(now),
});

/** The last months: this month and the months before it, up to now */
export const lastMonths = (now: Date, months: number): IReportPeriod => ({
  from: new Date(now.getFullYear(), now.getMonth() - months + 1, 1),
  key: `months-${months}`,
  to: addDays(startOfDay(now), 1),
});

export const fiscalYear = (year: number): IReportPeriod => ({
  from: new Date(year, 9, 1),
  key: `fiscal-year-${year}`,
  to: new Date(year + 1, 9, 1),
});

/** A quarter of a fiscal year: 1 = October-December, 2 = January-March and so on */
export const fiscalQuarter = (
  year: number,
  quarter: number
): IReportPeriod => ({
  from: new Date(year, 9 + (quarter - 1) * 3, 1),
  key: `quarter-${year}-${quarter}`,
  to: new Date(year, 9 + quarter * 3, 1),
});

/** Days chosen from the calendar, both included; the days in either order */
export const customPeriod = (first: Date, last: Date): IReportPeriod => {
  const [start, end] = first <= last ? [first, last] : [last, first];
  return {
    from: startOfDay(start),
    key: 'custom',
    to: addDays(startOfDay(end), 1),
  };
};

/** The months the period touches, e.g. for its columns */
export const monthsOf = (period: IReportPeriod): Date[] => {
  const months: Date[] = [];
  let month = new Date(period.from.getFullYear(), period.from.getMonth(), 1);
  while (month < period.to) {
    months.push(month);
    month = new Date(month.getFullYear(), month.getMonth() + 1, 1);
  }
  return months;
};

/** The days of the period, for a short period */
export const daysOf = (period: IReportPeriod): Date[] => {
  const days: Date[] = [];
  for (
    let day = startOfDay(period.from);
    day < period.to;
    day = addDays(day, 1)
  ) {
    days.push(day);
  }
  return days;
};
